import { codeFont } from '../../utils/fonts';
import { formatLaborHours } from '../../utils/laborTime';
import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CategoryIcon from '@mui/icons-material/Category';
import { withTranslation } from 'react-i18next';

const priceTypeLabels = {
  fixed: 'Fixed price',
  hourly: 'Hourly rate',
  variable: 'Variable',
};

const JobDetailDialog = ({ t, open, onClose, job, onEdit }) => {
  if (!job) return null;

  const formatPrice = (price, priceType) => {
    if (price === null || price === undefined) return '-';
    const formatted = new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
    if (priceType === 'hourly') {
      return `${formatted}/${t('h')}`;
    }
    return formatted;
  };

  // Stored in minutes, shown in decimal hours: 90 -> "1,5 h"
  const formatDuration = (minutes) => {
    if (!minutes) return t('Not set');
    return t('laborTime.hours', { hours: formatLaborHours(minutes) });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pb: 1,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '20px' }}>
              {job.name}
            </Typography>
            {job.job_code && (
              <Typography
                sx={{
                  fontSize: '13px',
                  ...codeFont,
                  color: 'primary.main',
                  bgcolor: 'primary.50',
                  px: 1,
                  py: 0.25,
                  borderRadius: '4px',
                }}
              >
                {job.job_code}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              label={t(priceTypeLabels[job.price_type] || 'Fixed price')}
              size="small"
              variant="outlined"
              sx={{
                borderColor: job.price_type === 'hourly' ? 'info.main' : 'divider',
                color: job.price_type === 'hourly' ? 'info.main' : 'text.secondary',
              }}
            />
            {job.category_name && (
              <Chip
                icon={<CategoryIcon sx={{ fontSize: 14 }} />}
                label={job.category_name}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Price Section */}
        <Box
          sx={{
            p: 2.5,
            bgcolor: 'background.default',
            borderRadius: '12px',
            mb: 3,
            textAlign: 'center',
          }}
        >
          <Typography color="text.secondary" sx={{ fontSize: '13px', mb: 0.5 }}>
            {job.price_type === 'hourly' ? t('Hourly Rate') : t('Price')}
          </Typography>

          {/* VAT-exclusive price (primary) */}
          <Typography
            sx={{
              fontSize: '28px',
              fontWeight: 700,
              color: 'primary.main',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {formatPrice(job.price, job.price_type)}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
            {t('excl. VAT')}
          </Typography>

          {/* VAT-inclusive price (secondary) */}
          {job.price !== null && job.price !== undefined && (
            <>
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: 'text.secondary',
                  mt: 1,
                }}
              >
                {formatPrice(
                  job.stored_price_incl_vat || (parseFloat(job.price) * (1 + (parseFloat(job.vat_rate) || 25.5) / 100)),
                  job.price_type
                )}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                {t('incl. VAT')} ({job.vat_rate || 25.5}%)
              </Typography>
            </>
          )}
        </Box>

        {/* Duration */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 2,
            bgcolor: 'background.default',
            borderRadius: '12px',
            mb: 3,
          }}
        >
          <AccessTimeIcon sx={{ color: 'text.secondary' }} />
          <Box>
            <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
              {t('Estimated Duration')}
            </Typography>
            <Typography sx={{ fontWeight: 500 }}>
              {formatDuration(job.estimated_duration)}
            </Typography>
          </Box>
        </Box>

        {/* Description */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary', mb: 1, textTransform: 'uppercase' }}
          >
            {t('Description')}
          </Typography>
          <Typography sx={{ color: 'text.primary' }}>
            {job.description || t('No description')}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Additional Info */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography color="text.secondary" sx={{ fontSize: '12px', mb: 0.5 }}>
              {t('Category')}
            </Typography>
            <Typography sx={{ fontWeight: 500 }}>
              {job.category_name || t('Uncategorized')}
            </Typography>
          </Box>
          <Box>
            <Typography color="text.secondary" sx={{ fontSize: '12px', mb: 0.5 }}>
              {t('Job Code')}
            </Typography>
            <Typography
              sx={{
                fontWeight: 500,
                ...(job.job_code ? codeFont : {}),
                color: job.job_code ? 'primary.main' : 'text.secondary',
              }}
            >
              {job.job_code || '-'}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: '10px',
            px: 3,
          }}
        >
          {t('Close')}
        </Button>
        <Button
          variant="contained"
          onClick={onEdit}
          startIcon={<EditIcon />}
          sx={{
            borderRadius: '10px',
            px: 3,
            bgcolor: 'secondary.main',
            '&:hover': { bgcolor: 'secondary.dark' },
          }}
        >
          {t('Edit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withTranslation()(JobDetailDialog);
